import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const RATES_CACHE_TTL_MS = 3000;

/** @type {Map<string, { promise: Promise<any>, time: number }>} */
const ratesCache = new Map();

/**
 * Shares one shipping_rates request between every <cart-delivery-fees> instance
 * (mini cart, drawer, cart page) and between the several triggers that fire for
 * a single cart change (cart:update, morph reconnect, explicit refresh()).
 * @param {string} url
 * @param {string} cacheKey
 * @returns {Promise<any>}
 */
function fetchShippingRates(url, cacheKey) {
  const cached = ratesCache.get(cacheKey);
  if (cached && Date.now() - cached.time < RATES_CACHE_TTL_MS) return cached.promise;

  const promise = fetch(url, { method: 'GET', headers: { Accept: 'application/json' } }).then((response) => {
    if (!response.ok) {
      throw new Error(`Shipping rates request failed with status ${response.status}`);
    }
    return response.json();
  });

  ratesCache.set(cacheKey, { promise, time: Date.now() });
  // Never keep a failed request around, the next trigger should retry.
  promise.catch(() => ratesCache.delete(cacheKey));

  return promise;
}

class CartDeliveryFees extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | null} */
  #refreshFrame = null;

  connectedCallback() {
    this.#scheduleRefresh();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
  }

  /**
   * Public refresh hook for cart morph updates.
   */
  refresh() {
    this.#scheduleRefresh();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.#refreshFrame !== null) {
      cancelAnimationFrame(this.#refreshFrame);
      this.#refreshFrame = null;
    }
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#handleCartUpdate);
  }

  /**
   * Triggers a delayed refresh so the cart DOM can finish morphing first.
   */
  #scheduleRefresh() {
    if (this.#refreshFrame !== null) {
      cancelAnimationFrame(this.#refreshFrame);
    }

    this.#refreshFrame = requestAnimationFrame(() => {
      this.#refreshFrame = null;
      this.#loadShippingRates();
    });
  }

  /**
   * Refreshes shipping rates after cart changes.
   */
  #handleCartUpdate = () => {
    this.#scheduleRefresh();
  };

  async #loadShippingRates() {
    this.#abortController?.abort();
    this.#abortController = null;
    const valueElement = this.#valueElement();
    const totalElement = this.#totalElement();
    const thresholdValue = this.dataset.freeShippingThreshold?.trim();
    const threshold = Number(thresholdValue);
    // Same base as the progress bar and checkout extension: the total after discounts.
    // Rates from /cart/shipping_rates.json exclude automatic free-shipping discounts, so they can't be used here.
    if (
      thresholdValue &&
      Number.isFinite(threshold) &&
      threshold >= 0 &&
      this.#baseTotal() >= Math.round(threshold * 100)
    ) {
      // Real fee reported back by the checkout extension when the threshold didn't grant free shipping.
      const chargedFee = this.#parseShippingPrice(this.dataset.chargedShippingFee);
      this.#setValueAndTotal(chargedFee > 0 ? chargedFee : 0);
      valueElement?.removeAttribute('shimmer');
      totalElement?.removeAttribute('shimmer');
      return;
    }
    if (valueElement instanceof HTMLElement) {
      valueElement.setAttribute('shimmer', '');
    }
    if (totalElement instanceof HTMLElement) {
      totalElement.setAttribute('shimmer', '');
    }

    const shippingQuery = this.#shippingQuery();
    if (!shippingQuery) {
      // No saved address: mirror checkout instead of guessing a destination.
      this.#setValueAndTotal(null, this.dataset.noAddressLabel);
      valueElement?.removeAttribute('shimmer');
      totalElement?.removeAttribute('shimmer');
      return;
    }

    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      // The cart total is part of the key so a changed cart never reuses old rates.
      const url = `/cart/shipping_rates.json?${shippingQuery.toString()}`;
      const data = await fetchShippingRates(url, `${url}|${this.#baseTotal()}`);
      if (this.#abortController !== abortController) return;
      const shippingRate = this.#cheapestShippingRate(data?.shipping_rates);
      const priceValue = shippingRate ? this.#parseShippingPrice(shippingRate.price) : null;

      this.#setValueAndTotal(priceValue);
    } catch (error) {
      if (this.#abortController === abortController && !abortController.signal.aborted) {
        this.#setValueAndTotal(null);
      }
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
        valueElement?.removeAttribute('shimmer');
        totalElement?.removeAttribute('shimmer');
      }
    }
  }

  /**
   * Returns the target element that displays the delivery fee value.
   * @returns {HTMLElement | null}
   */
  #valueElement() {
    const valueElement = this.querySelector('[data-delivery-fees-value]');
    return valueElement instanceof HTMLElement ? valueElement : null;
  }

  /**
   * Returns the target element that displays the cart total value.
   * @returns {HTMLElement | null}
   */
  #totalElement() {
    const container = this.closest('.cart-summary-panel, .mini-cart');
    if (!(container instanceof HTMLElement)) return null;

    const totalElement = container.querySelector('[data-cart-total-value]');
    return totalElement instanceof HTMLElement ? totalElement : null;
  }

  /**
   * Builds the shipping query payload.
   * @returns {URLSearchParams | null}
   */
  #shippingQuery() {
    // Guest defaults are resolved server-side (market country + theme settings).
    const country = (this.dataset.shippingCountry || '').trim();
    const province = (this.dataset.shippingProvince || '').trim();
    const zip = (this.dataset.shippingZip || '').trim();

    // Only the country is required; province/zip are optional so stores without default settings still query rates.
    if (!country) return null;

    const query = new URLSearchParams({ 'shipping_address[country]': country });
    if (province) query.set('shipping_address[province]', province);
    if (zip) query.set('shipping_address[zip]', zip);

    return query;
  }

  /**
   * Picks the cheapest shipping rate from the API response.
   * @param {Array<{ price?: string | number }>} shippingRates
   * @returns {{ price?: string | number } | null}
   */
  #cheapestShippingRate(shippingRates) {
    if (!Array.isArray(shippingRates) || shippingRates.length === 0) return null;

    return shippingRates.reduce((cheapest, rate) => {
      const currentPrice = this.#parseShippingPrice(rate?.price);
      if (!Number.isFinite(currentPrice)) return cheapest;
      if (!cheapest) return rate;

      const cheapestPrice = this.#parseShippingPrice(cheapest.price);
      return currentPrice < cheapestPrice ? rate : cheapest;
    }, /** @type {{ price?: string | number } | null} */ (null));
  }

  /**
   * Parses a shipping rate price into minor units.
   * @param {string | number | undefined} price
   * @returns {number}
   */
  #parseShippingPrice(price) {
    const currency = (this.dataset.currency || 'THB').toUpperCase();
    // Must share formatMoney's precision table: Intl reports 0 decimals for IDR, Shopify uses 2.
    const minorUnits = convertMoneyToMinorUnits(String(price ?? ''), currency);

    return minorUnits ?? NaN;
  }

  /**
   * Formats a minor-unit value using the storefront money format.
   * @param {number} value
   * @returns {string}
   */
  #formatMoney(value) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'THB';
    return formatMoney(value, format, currency);
  }

  /**
   * Reads the base cart total from the element dataset.
   * @returns {number}
   */
  #baseTotal() {
    const value = Number(this.dataset.baseTotal || 0);
    return Number.isFinite(value) ? value : 0;
  }

  /**
   * Updates the displayed delivery fee value and cart total.
   * @param {number | null} shippingFee
   * @param {string} [unavailableLabel] overrides the default label when no fee is shown
   */
  #setValueAndTotal(shippingFee, unavailableLabel) {
    const shippingValue = Number.isFinite(shippingFee) ? shippingFee : 0;
    const totalValue = this.#baseTotal() + shippingValue;
    const value = shippingFee !== null && Number.isFinite(shippingFee)
      ? this.#formatMoney(shippingValue)
      // Checkout shows "Enter shipping address" until it has a rate, so never fall back to "Calculated at checkout".
      : unavailableLabel || this.dataset.noAddressLabel || '';
    const total = this.#formatMoney(totalValue);

    const valueElement = this.#valueElement();
    if (valueElement) {
      valueElement.textContent = value;
      valueElement.setAttribute('value', value);
    }

    const totalElement = this.#totalElement();
    if (totalElement) {
      totalElement.textContent = total;
      totalElement.setAttribute('value', total);
    }
  }

}

if (!customElements.get('cart-delivery-fees')) {
  customElements.define('cart-delivery-fees', CartDeliveryFees);
}
